import React, { useId, useRef, useState } from 'react';
import { Image, Link, UploadCloud, X } from 'lucide-react';

interface ImageUploadProps {
  imageUrl: string;
  imagePreview: string;
  imageFile: File | null;
  uploading: boolean;
  onImageUrlChange: (value: string) => void;
  onFileSelect: (file: File) => void;
  onUpload: () => void;
  onClear: () => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  imageUrl,
  imagePreview,
  imageFile,
  uploading,
  onImageUrlChange,
  onFileSelect,
  onUpload,
  onClear,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputId = useId();
  const [dragging, setDragging] = useState(false);
  const previewSrc = imagePreview || imageUrl;

  const handleFiles = (files: FileList | File[]) => {
    const file = files[0];
    if (file) {
      onFileSelect(file);
    }
  };

  return (
    <div className="image-upload-section">
      <div className="image-upload-url-shell">
        <Link className="image-upload-url-icon" />
        <input
          type="text"
          value={imageUrl}
          onChange={(event) => onImageUrlChange(event.target.value)}
          placeholder="Paste image URL or upload below"
        />
      </div>

      <label
        htmlFor={fileInputId}
        role="button"
        tabIndex={0}
        className={`image-dropzone ${dragging ? 'dragging' : ''}`}
        aria-label="Drag and drop market image"
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          handleFiles(event.dataTransfer.files);
        }}
      >
        <UploadCloud className="image-dropzone__icon" />
        <span className="image-dropzone__title">Drag & drop image here</span>
        <span className="image-dropzone__hint">or choose JPG, PNG, or WebP up to 2MB</span>
        <span className="image-dropzone__choose">Choose File</span>
      </label>

      <input
        id={fileInputId}
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        aria-label="Choose File"
        onChange={(event) => {
          if (event.target.files) {
            handleFiles(event.target.files);
          }
        }}
        className="image-upload-file-input"
      />

      {previewSrc && (
        <div className="image-upload-preview-shell">
          <div className="image-upload-preview">
            <div className="image-upload-preview__header">
              <span className="image-upload-preview__title">
                <Image />
                <span>{imageFile ? imageFile.name : 'Image preview'}</span>
              </span>
              <button
                type="button"
                className="image-upload-clear-button"
                aria-label="Discard image"
                onClick={() => {
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }

                  onClear();
                }}
              >
                <X />
              </button>
            </div>
            <img src={previewSrc} alt="Market preview" />
          </div>
        </div>
      )}

      {imageFile && (
        <button
          type="button"
          onClick={onUpload}
          disabled={uploading}
          className="upload-submit-button"
          aria-label="Upload image"
        >
          {uploading ? 'Uploading...' : 'Upload Image'}
        </button>
      )}
    </div>
  );
};

export default ImageUpload;
